import { useEffect, useMemo, useState } from 'react';
import services from '../config/services.json';
import genres from '../config/genres.json';
import { tmdb } from '../lib/tmdb.js';
import ServiceChips from '../components/ServiceChips.jsx';
import Shelf from '../components/Shelf.jsx';
import StaffPicks from '../components/StaffPicks.jsx';
import ForYouShelf from '../components/ForYouShelf.jsx';
import MovieModal from '../components/MovieModal.jsx';
import AdminInvite from '../components/AdminInvite.jsx';

export default function TheFloor() {
  const [selectedServices, setSelectedServices] = useState([]); // [] === all
  const [nowPlaying, setNowPlaying] = useState([]);
  const [aisles, setAisles] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const providerIds = useMemo(() => {
    const list = services.services.filter((s) => s.active);
    const wanted = selectedServices.length ? selectedServices : list.map((s) => s.id);
    return list
      .filter((s) => wanted.includes(s.id))
      .map((s) => s.tmdbProviderId)
      .join('|');
  }, [selectedServices]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    async function load() {
      // Now Showing: discover with primary_release in the last ~120 days,
      // filtered by selected providers.
      const today = new Date();
      const lookback = new Date(today.getTime() - 1000 * 60 * 60 * 24 * 120);
      const fmt = (d) => d.toISOString().slice(0, 10);

      const nowReq = tmdb.discover({
        providers: providerIds,
        yearGte: lookback.getFullYear(),
        sort: 'primary_release_date.desc',
        media: 'movie',
      });

      const aisleReqs = genres.aisles.map((a) => {
        const params = { providers: providerIds, sort: 'popularity.desc', media: 'all' };
        if (a.tmdbGenreId) params.genre = a.tmdbGenreId;
        if (a.filterRule === 'non-english') params.lang = 'ja'; // approximation: feature foreign rotation
        return tmdb.discover(params).then((data) => [a.id, data.results || []]);
      });

      const [nowData, ...aisleEntries] = await Promise.all([nowReq, ...aisleReqs]);
      if (!alive) return;
      setNowPlaying(nowData.results || []);
      setAisles(Object.fromEntries(aisleEntries));
      setLoading(false);
    }

    load().catch((err) => {
      console.error(err);
      if (alive) setLoading(false);
    });

    return () => { alive = false; };
  }, [providerIds]);

  const toggle = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <>
      <div className="hb-floor__chips">
        <ServiceChips
          selected={selectedServices}
          onToggle={toggle}
          onAll={() => setSelectedServices([])}
        />
      </div>

      <AdminInvite />

      <Shelf
        title="Now Showing"
        subtitle="New this week across your services"
        loading={loading}
        movies={nowPlaying.slice(0, 20)}
        onPosterClick={(m) => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
      />

      <ForYouShelf
        providerIds={providerIds}
        onPosterClick={(m) => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
      />

      <StaffPicks onPosterClick={(m) => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })} />

      {genres.aisles.map((aisle) => (
        <Shelf
          key={aisle.id}
          title={aisle.name}
          loading={loading}
          movies={(aisles[aisle.id] || []).slice(0, 20)}
          onPosterClick={(m) => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
        />
      ))}

      {openId && <MovieModal movieId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
